/**
 * The top bar (13f, rebuilt): where you are, how to get anywhere, and what
 * needs you.
 *
 *   left   menu button (< 1024px) · page title · business · branch
 *   right  Go to… (Ctrl/⌘ K) · shift · approvals · notifications · team
 *          chat · account menu (profile, settings, display currency,
 *          activity, help, log out)
 *
 * Removed: the date and time chips (a clock re-drew the whole bar every
 * second), the messages panel that looked like chat but never sent
 * anything (team chat has its own page), the activity panel that polled
 * every minute (Activity is a page), and shortcuts that went to a page
 * that doesn't exist (/report). On narrow screens the least-used controls
 * move into the account menu instead of squeezing the business name.
 */
import React, { ReactNode } from "react";
import { BarChart, History, LifeBuoy, LogOut, MessageCircle, Settings as SettingsIcon, User as UserIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useAuthStore, { isAnyAdmin, type UserRole } from "@/stores/authStore";
import useChatStore from "@/stores/chatStore";
import { BusinessSwitcher } from "./BusinessSwitcher";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { BranchSwitcher } from "./BranchSwitcher";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationStack from "@/components/notifications/NotificationStack";
import HelpMenu from "@/components/help/HelpMenu";
import ApprovalPrompt from "@/components/approvals/ApprovalPrompt";
import CurrentShiftBadge from "@/components/shifts/CurrentShiftBadge";
import { CurrencySwitcher } from "../group/CurrencySwitcher";
import { usePermissions } from "@/hooks/usePermissions";
import { useTeamChatAvailable } from "@/hooks/useTeamChatAvailable";
import QuickFind from "./QuickFind";

// ─── Role display labels — kept in sync with Sidebar.tsx ────────────
const ROLE_LABELS: Record<UserRole, string> = {
  tenant_owner: "Tenant Owner",
  super_admin: "Super Admin",
  business_admin: "Business Administrator",
  staff: "Staff",
  cashier: "Cashier",
};

interface HeaderProps {
  title: string;
  children?: ReactNode;
}

function getInitials(name: string | undefined): string {
  if (!name) return "U";
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]!)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const iconBtn =
  "relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

export const Header: React.FC<HeaderProps> = ({ title, children }) => {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const effRole = useEffectiveRole() || user?.role;
  const isAdmin = effRole ? isAnyAdmin(effRole) : false;
  const { can } = usePermissions();
  const chatOn = useTeamChatAvailable() && can("chat.message.read");
  const unread = useChatStore((s) => Object.values(s.unreadCounts).reduce((sum, n) => sum + n, 0));

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out. Please try again.");
    }
  };

  return (
    <header className="sticky top-0 z-40 flex h-14 w-full items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:h-16 sm:px-4 lg:px-6">
      {/* Where you are */}
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        {children}
        <h1 className="hidden max-w-[16rem] shrink truncate text-base font-semibold text-foreground md:block lg:text-lg" title={title}>
          {title}
        </h1>
        <span className="hidden h-5 w-px shrink-0 bg-border md:block" aria-hidden />
        <div className="flex min-w-0 items-center gap-1">
          <BusinessSwitcher />
          <BranchSwitcher />
        </div>
      </div>

      {/* How to get anywhere, and what needs you */}
      <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
        <QuickFind />
        <CurrentShiftBadge />
        <div className="hidden xl:flex">
          <CurrencySwitcher />
        </div>
        <ApprovalPrompt />
        <NotificationBell />
        <NotificationStack />
        {chatOn && (
          <Link to="/team-chat" className={`${iconBtn} hidden sm:inline-flex`} aria-label={unread ? `Team chat, ${unread} unread` : "Team chat"} title="Team chat">
            <MessageCircle className="h-4 w-4" aria-hidden />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold tabular-nums text-white">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
        )}
        <div className="hidden lg:flex">
          <HelpMenu />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="relative ml-0.5 flex shrink-0 items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label="Account menu"
            >
              <Avatar className="h-8 w-8 ring-2 ring-border transition-colors hover:ring-primary/40 sm:h-9 sm:w-9">
                <AvatarImage src={user?.profileImage} />
                <AvatarFallback className="bg-accent text-xs font-medium text-accent-foreground">{getInitials(user?.name)}</AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {user && (
              <>
                <DropdownMenuLabel className="py-2.5 font-normal">
                  <div className="flex flex-col space-y-0.5">
                    <p className="truncate text-sm font-medium leading-none">{user.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => navigate("/settings?tab=profile")}>
              <UserIcon className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <SettingsIcon className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem onClick={() => navigate("/reports")}>
                <BarChart className="mr-2 h-4 w-4" />
                <span>Reports</span>
              </DropdownMenuItem>
            )}
            {can(["activity.read.all", "activity.read.own"]) && (
              <DropdownMenuItem onClick={() => navigate("/action-history")}>
                <History className="mr-2 h-4 w-4" />
                <span>Activity</span>
              </DropdownMenuItem>
            )}
            {chatOn && (
              <DropdownMenuItem className="sm:hidden" onClick={() => navigate("/team-chat")}>
                <MessageCircle className="mr-2 h-4 w-4" />
                <span>Team chat{unread ? ` (${unread})` : ""}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="lg:hidden" onClick={() => navigate("/help")}>
              <LifeBuoy className="mr-2 h-4 w-4" />
              <span>Help</span>
            </DropdownMenuItem>
            {/* Display currency: in the bar on wide screens, here otherwise. */}
            <div className="px-2 py-1.5 xl:hidden">
              <p className="mb-1 text-xs text-muted-foreground">Show amounts in</p>
              <CurrencySwitcher />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:bg-destructive/10 focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
